import { createChart, type IChartApi, type ISeriesApi, type MouseEventParams, type Time, type SeriesDefinition, ColorType } from "lightweight-charts";
import { Chart } from "../../../models/historical-charts/historical-chart.models";
import { TimeRange } from "../../../models/historical-charts/time-range.enum";
import { Series, SeriesTypeEnum, isCustomSeries } from "../../../models/historical-charts/series";

import { ChartLegendComponent } from "../chart-legend/chart-legend.component"
import { SeriesListComponent } from "../chart-series/chart-series-list.component"
import { TimeRangeComponent, TimeRangeChangeRequestEvent } from "../time-range.component/chart-time-range.component";
import { StackedAreaSeries } from "../../../options/historical-charts/stacked-area-series-plugin/stacked-area-series";
import { transformToStackedAreaData } from "../../../data/stacked-area.data";
import { ChartType } from "../../../models/historical-charts/historical-chart.enum";
import { LoaderComponent } from "../../../core/components/loader/loader-component"
import { type StackedAreaColor } from "../../../models/historical-charts/colors.values";
import { seriesOptionsMap } from "../chart-series/series-options.values";
import { ChartWatermarkComponent } from "../chart-watermark/chart-watermark.component";
import { getTimeRanges } from "../../../options/historical-charts/historical-chart.options";
import { debounce } from "../../../../core/utils/debounce";

import styleCSS from "./chart.style.scss?inline"
import templateHTML from './chart.component.html?raw'
import type { ChartService } from "../../../services/historical-chart.service";
import type { AuthService } from "../../../../auth/services/auth.service";

export class HistoricalChartComponent extends HTMLElement {
  private _setSeriesFn!: (series: Series[]) => void
  private _viewSeriesMap: Map<string, ISeriesApi<any>> = new Map()
  private _chart!: Chart
  private _view!: IChartApi
  private _legend!: ChartLegendComponent
  private _seriesList!: SeriesListComponent
  private _timeRangeComponent: TimeRangeComponent | undefined
  private _sourceDiv!: HTMLDivElement
  private _graphBottom!: HTMLDivElement
  private _graphWrapper!: HTMLDivElement
  private _loader!: LoaderComponent
  private _graphTop!: HTMLDivElement

  constructor(
    private chartService:ChartService = window.tbwServices.chartService, 
    private authService:AuthService = window.tbwServices.authService, 
  ) { super(); }

  connectedCallback() {
    const shadowRoot = this.attachShadow({ mode: 'open' })
    const container = document.createElement('div')
    container.innerHTML = templateHTML
    const template = container.querySelector('template')
    const style = document.createElement('style')
    style.textContent = styleCSS
    shadowRoot.appendChild(style)
    shadowRoot.appendChild(template!.content.cloneNode(true))

    const chartTypeAttribute = this.attributes.getNamedItem('chart-type')
    const seriesTypeAttribute = this.attributes.getNamedItem('series-type')
    const autoloadAttribute = this.attributes.getNamedItem('auto-load')

    if (!chartTypeAttribute || !seriesTypeAttribute) { throw new Error('missing chart and/or series type attributes') }

    const chartType = chartTypeAttribute.value as ChartType
    const seriesType = seriesTypeAttribute.value as SeriesTypeEnum
    const timeRange = getTimeRanges(chartType).includes(TimeRange.Year) ? TimeRange.Year : getTimeRanges(chartType)[0]
    const autoLoad = autoloadAttribute?.value || "true"

    this.setupGUI(shadowRoot)
    this._chart = new Chart(chartType, seriesType, timeRange);

    if (this._chart.timeRanges.length > 1) {
      this._timeRangeComponent = document.createElement('tbw-chart-time-range') as TimeRangeComponent
      this._graphBottom.insertBefore(this._timeRangeComponent, this._sourceDiv)
      this._timeRangeComponent.addEventListener(TimeRangeChangeRequestEvent.EVENT_NAME, this.timeChangeRequestHandler.bind(this))
    }

    // Only auto-load if auth state is established, otherwise wait for authChange event
    if (autoLoad !== 'false' && this.authService.profile) {
      // Small delay to ensure theme is properly initialized
      setTimeout(() => this.init(), 0)
    }

    this._onAuthChanged = this.onAuthChanged.bind(this)
    document.addEventListener('authChange', this._onAuthChanged)
  }

  private _onAuthChanged!: (e: Event) => void

  onAuthChanged(_: Event): void {
    // If chart hasn't been initialized yet, initialize it first
    if (!this._view) {
      this.init()
    } else {
      // console.log(this._view)
      this.refresh()
    }
  }

  private getCssVariable(variable: string) {
    return getComputedStyle(document.body).getPropertyValue(variable);
  }

  private applyCurrentTheme(): void {
    if (!this._view) return

    // Force a small delay to ensure CSS variables are updated
    setTimeout(() => {
      this._view.applyOptions({
        layout: {
          background: {
            type: ColorType.Solid, color: this.getCssVariable("--seashell")
          },
          textColor: this.getCssVariable("--midnight-blue")
        },
        grid: {
          horzLines: {
            visible: true,
            color: this.getCssVariable("--light-grey")
          },
          vertLines: {
            visible: true,
            color: this.getCssVariable("--light-grey")
          }
        }
      })
    }, 10)
  }

  setupGUI(shadowRoot: ShadowRoot): void {
    this._graphTop = shadowRoot.querySelector('.graph-top')!
    this._graphWrapper = shadowRoot.querySelector('.graph-wrapper')!
    this._legend = shadowRoot.querySelector('tbw-chart-legend') as ChartLegendComponent
    this._graphBottom = shadowRoot.querySelector('.graph-bottom')!
    this._sourceDiv = shadowRoot.querySelector('.source')!
    const loaderElement = shadowRoot.querySelector('.tbw-loader') as HTMLElement;
    this._loader = new LoaderComponent(loaderElement);
  }

  timeChangeRequestHandler(e: Event) {
    this.setTimeRange((e as TimeRangeChangeRequestEvent).timeRange)
  }

  disconnectedCallback() {
    if (this._timeRangeComponent) { this._timeRangeComponent.removeEventListener(TimeRangeChangeRequestEvent.EVENT_NAME, this.timeChangeRequestHandler) }
    document.removeEventListener('authChange', this._onAuthChanged)
  }

  async init() {
    this._setSeriesFn = this.getSeriesFunction()
    // console.log(this._chart.chartOptions);
    this._view = createChart(this._graphWrapper, this._chart.chartOptions)
    
    if (this._legend) {
      this._view.subscribeCrosshairMove((param: MouseEventParams<Time>) =>
        this._legend.update(param, this._chart, this._viewSeriesMap, this._view.options().localization.priceFormatter!))
    }

    // Apply current theme immediately after chart creation
    this.applyCurrentTheme()

    const container = this._graphWrapper
    const handleResize = debounce(() => {
      this._view.timeScale().fitContent()
    }, 100) // 100ms = délai raisonnable

    // Observe les changements de taille du container
    const resizeObserver = new ResizeObserver(handleResize)
    resizeObserver.observe(container)

    await this.refresh()

    this._seriesList = new SeriesListComponent()
    this._graphTop.appendChild(this._seriesList)
    this._seriesList.init(this._chart.series)
    const watermark = new ChartWatermarkComponent();
    this._graphWrapper.appendChild(watermark);
  }

  private getSeriesFunction(): (series: Series[]) => void {
    if (this.isCustomSeries()) {
      return this.addCustomSeries
    } else if (this.isDualAxisChart()) {
      return this.addDualSeries
    } else {
      return this.addSeriesArray
    }
  }

  private isDualAxisChart(): boolean {
    const chartOptions = this._chart.chartOptions

    return chartOptions.rightPriceScale?.visible === true &&
      chartOptions.leftPriceScale?.visible === true
  }

  async refresh() {
    await this.load()
    // if (!permissionService.hasFullAccess('charts', '')) {
    //   this._chart.timeRange = this._chart.timeRanges[0]
    // }
    this.bindToView()
  }

  private async load() {
    this._loader.show()
    await this.chartService.load(this._chart)
    this._loader.hide()
  }
  get series(): Array<ISeriesApi<any>> {
    return Array.from(this._viewSeriesMap.values())
  }

  getHiddenSeriesNames(viewSeriesMap: Map<string, ISeriesApi<any>>): string[] {
    const hiddenSeries: string[] = []
    for (const [name, series] of viewSeriesMap) {
      if (!series.options().visible) { hiddenSeries.push(name) }
    }
    return hiddenSeries
  }

  private bindToView() {

    if (this._timeRangeComponent) {
      // this._timeRangeComponent.setAttribute(TimeRangeComponent.ATTRIBUTE_AUTH, this._authService.hasFullAccess('charts').toString())
      this._timeRangeComponent.setAttribute(TimeRangeComponent.ATTRIBUTE_AUTH, "true");
      this._timeRangeComponent.setAttribute(TimeRangeComponent.SELECTED_ATTR, this._chart.timeRange)
    }

    // backup and restore series hidden states
    const hiddenSeriesNames: string[] = this.getHiddenSeriesNames(this._viewSeriesMap)

    this.clearSeries()
    this._setSeriesFn(this._chart.series)

    hiddenSeriesNames.forEach(s => this._viewSeriesMap.get(s)!.applyOptions({ visible: false }))
    
    // FIXED BY AI: reset time scale to the first and last point of the first populated series
    const firstPopulatedSeries = this._chart.series.find(series => series.points.length > 0)
    if (firstPopulatedSeries) {
      const firstPoint = firstPopulatedSeries.points[0]
      const lastPoint = firstPopulatedSeries.points[firstPopulatedSeries.points.length - 1]
      this._view.timeScale().setVisibleRange({
        from: firstPoint.time,
        to: lastPoint.time
      })
      
    } else {
      this._view.timeScale().resetTimeScale()
    }
    this._sourceDiv.innerHTML = `<p>source: ${this._chart.source}</p>`


  }

  private addSeriesArray(array: Series[]) {
    array.forEach(this.addSeries.bind(this))
  }

  private addDualSeries(array: Series[]) {
    array.forEach((s, index) => {
      const seriesOptions = this.generateSeriesOptions(s)

      const priceScaleId = this.getDualAxisAssignment(s, index)
      if (priceScaleId) {
        (seriesOptions as any).priceScaleId = priceScaleId
      }

      const viewSeries: ISeriesApi<any> = this._view.addSeries(this._chart.seriesDefinitions as SeriesDefinition<any>, seriesOptions)
      viewSeries.setData(s.points)

      this._viewSeriesMap.set(s.name, viewSeries)
    })
  }

  private getDualAxisAssignment(_series: Series, index: number): string | null {
    if (index === 0) {
      return 'left'
    } else if (index === 1) {
      return 'right'
    }
    return null
  }

  private addCustomSeries(seriesArray: Series[]) {
    // Don't add series if there's no data
    if (!seriesArray || seriesArray.length === 0) {
      return;
    }

    const viewSeries = this._view.addCustomSeries(new StackedAreaSeries())
    viewSeries.setData(transformToStackedAreaData(seriesArray))
    viewSeries.applyOptions({
      colors: seriesArray.map(s => ({ color: s.options.color, line: s.options.color, area: s.options.color })) as StackedAreaColor[],
    })
    this._viewSeriesMap.set('stacked', viewSeries)
    return viewSeries
  }

  private isCustomSeries(): boolean {
    return isCustomSeries(this._chart.seriesDefinitions)
  }

  private addSeries(s: Series): ISeriesApi<any> {
    // console.log(s.options);
    const viewSeries: ISeriesApi<any> = this._view.addSeries(this._chart.seriesDefinitions as SeriesDefinition<any>, this.generateSeriesOptions(s))
    viewSeries.setData(s.points)

    this._viewSeriesMap.set(s.name, viewSeries)
    return viewSeries
  }

  private clearSeries() {
    for (const [_, series] of this._viewSeriesMap) { this._view.removeSeries(series) }
    this._viewSeriesMap.clear()
  }

  async setTimeRange(timeRange: TimeRange): Promise<void> {
    this._chart.timeRange = timeRange
    await this.refresh()
  }

  private generateSeriesOptions(series: Series) {
    return Object.assign({}, series.options, seriesOptionsMap.get(this._chart.seriesType))
  }
}